
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Paintbrush, ArrowRight, Shield, Clock, CheckCircle, Gauge, Calculator, FileText, Wrench, Hammer, ArrowLeft, Users, Award, Star, ShieldCheck, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import Image from "@/components/ui/image";
import { useIsMobile } from "@/hooks/use-mobile";
import { LainaCta } from "@/components/common/LainaCta";
import EnhancedSeoMetadataComponent from "@/components/common/EnhancedSeoMetadata";
import { getEnhancedSeoDataForRoute } from "@/utils/enhancedSeoUtils";
import RenovationLoanCalculator from '@/components/loan/RenovationLoanCalculator';
import { ZatapAdvertisement } from '@/components/loan/renovation/ZatapAdvertisement';
import RakennustarvikeAds from '@/components/common/RakennustarvikeAds';
import { LoanBenefitsSection } from '@/components/loan/LoanBenefitsSection';
import { RenovationExamplesSection } from '@/components/loan/renovation/RenovationExamplesSection';
import { RenovationHeroBanner } from '@/components/loan/renovation/RenovationHeroBanner';
import { preloadCriticalImages } from '@/utils/imageOptimizer';
import RenovationImageGallery from '@/components/loan/renovation/RenovationImageGallery';
import UscoreAdvertisement from '@/components/home/UscoreAdvertisement';

const seoKeywords = ["remonttilaina", "remonttirahoitus", "kodin remontointi", "keittiöremontti", "kylpyhuoneremontti", "rahoitus remontille", "edullinen remonttilaina", "talon remontointi", "huoneistoremontti", "taloyhtiölaina", "remonttilainan korko", "remonttilainan hinta", "remontoinnin rahoitus", "energiaremontti", "asuntolainaremontti", "omarahoitteinen remontti"];
const criticalImages = ["/lovable-uploads/b154e542-619f-469d-b351-ed42be772da4.png", "/lovable-uploads/ac2a0d50-2fbb-4074-b64d-69559a411796.png", "/lovable-uploads/058e7e20-8c78-4a85-884b-b4d8155db8ac.png"];
const fallbackImages = {
  general: "https://images.unsplash.com/photo-1534349762230-e0cadf78f5da?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
  kitchen: "https://images.unsplash.com/photo-1556912998-c57cc6b63cd7?auto=format&fit=crop&w=600&q=80",
  bathroom: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=600&q=80",
  living: "https://images.unsplash.com/photo-1600121848594-d8644e57abab?auto=format&fit=crop&w=600&q=80"
};

const RemonttilainaPage = () => {
  const isMobile = useIsMobile();
  const seoData = getEnhancedSeoDataForRoute("/lainat/remonttilaina");
  
  useEffect(() => {
    preloadCriticalImages(criticalImages, true);
  }, []);
  
  return <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <EnhancedSeoMetadataComponent
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl="https://lainasampo.fi/lainat/remonttilaina"
        schema={seoData.schema}
      />

      <div className="container mx-auto px-4 py-4">
        <Link to="/lainatuotteet" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Takaisin lainatuotteisiin
        </Link>
      </div>

      <div className="relative h-[50vh] md:h-[55vh] min-h-[400px] max-h-[480px]">
        <Image 
          src="/lovable-uploads/b154e542-619f-469d-b351-ed42be772da4.png" 
          alt="Tyytyväinen pariskunta remontoimassa kotia" 
          className="absolute inset-0 w-full h-full object-cover object-center" 
          fallbackSrc="https://images.unsplash.com/photo-1534349762230-e0cadf78f5da?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" 
          priority={true} 
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/20">
          <div className="container mx-auto px-4 md:px-6 h-full flex items-center">
            <div className="max-w-lg text-white space-y-4">
              <h1 className="text-4xl sm:text-5xl md:text-4xl font-bold leading-tight">
                Toteuta <span className="text-blue-400">kodin unelmaremonttisi</span>
              </h1>
              <p className="text-lg md:text-lg leading-relaxed">
                Kilpailuta remonttiin tarvittava rahoitus ilmaisesti. Vertaile tarjouksia ja löydä edullisin ratkaisu.
              </p>
              <div className="pt-2">
                <LainaCta 
                  text={isMobile ? "Hae lainaa" : "Hae remonttilainaa"} 
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold shadow-lg transform hover:scale-105 transition duration-300" 
                  variant="primary" 
                  size={isMobile ? "md" : "lg"} 
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pt-10">
        {/* Enhanced benefits bar */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-wrap justify-center gap-2 md:gap-4 mb-10 md:mb-12 relative z-10"
        >
          {[
            { text: isMobile ? "Päätös 24h" : "Päätös 24h sisällä", icon: <Clock className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "Korot alk. 4,9%" : "Edulliset korot alkaen 4,9%", icon: <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "1k€ - 70k€" : "1 000€ - 70 000€", icon: <Shield className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "1-15v maksuaika" : "Maksuaika 1-15 vuotta", icon: <Gauge className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> }
          ].map((item, i) => (
            <motion.div 
              key={i} 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: i * 0.1 }}
              className="bg-white px-3 py-2 md:px-6 md:py-3 rounded-full shadow-md flex items-center gap-1.5 md:gap-2 text-sm md:text-base hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              {item.icon}
              <span className="font-medium">{item.text}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* Social proof section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-center mb-8 md:mb-12"
        >
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 md:p-8 border border-green-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-green-100 rounded-full -mt-10 -mr-10 opacity-50"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-center mb-4">
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <span className="ml-2 text-sm font-medium text-gray-600">4.8/5</span>
              </div>
              <p className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                Yli <span className="text-green-600 font-bold">50 000</span> tyytyväistä asiakasta
              </p>
              <p className="text-sm md:text-base text-gray-600">
                "Säästin 1 200€ remonttikuluissa kilpailuttamalla rahoituksen Lainasampo.fi:n kautta"
              </p>
              <div className="flex items-center justify-center mt-3">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 bg-blue-500 rounded-full border-2 border-white"></div>
                  <div className="w-8 h-8 bg-green-500 rounded-full border-2 border-white"></div>
                  <div className="w-8 h-8 bg-purple-500 rounded-full border-2 border-white"></div>
                  <div className="w-8 h-8 bg-orange-500 rounded-full border-2 border-white flex items-center justify-center text-xs text-white font-bold">
                    +50k
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-8 mb-8 md:mb-16">
          <div className="space-y-6">
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <div className="aspect-[4/3]">
                <Image src="/lovable-uploads/ac2a0d50-2fbb-4074-b64d-69559a411796.png" alt="Modernit kierreportaat" className="w-full h-full object-cover" fallbackSrc={fallbackImages.general} priority={true} />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-8">
                <div className="absolute bottom-8 left-8 right-8">
                  <h3 className="text-white text-2xl font-bold mb-4">Huolellinen suunnittelu</h3>
                  <ul className="mt-4 space-y-3">
                    {["Kustannusarvio ja budjetti", "Materiaalien valinta ja vertailu", "Ammattilaisten suositukset", "Aikataulun suunnittelu"].map((item, i) => <li key={i} className="flex items-center text-white text-base">
                        <CheckCircle className="h-5 w-5 mr-3 text-blue-400" />
                        <span>{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <div className="aspect-[4/3]">
                <Image src="/lovable-uploads/058e7e20-8c78-4a85-884b-b4d8155db8ac.png" alt="Moderni arkkitehtuuri" className="w-full h-full object-cover" fallbackSrc={fallbackImages.general} priority={true} />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-8">
                <div className="absolute bottom-8 left-8 right-8">
                  <h3 className="text-white text-2xl font-bold mb-4">Ammattilaisremontointi</h3>
                  <ul className="mt-4 space-y-3">
                    {["Luotettavat tekijät", "Laadun varmistus", "Kustannustehokkuus", "Takuutyöt"].map((item, i) => <li key={i} className="flex items-center text-white text-base">
                        <CheckCircle className="h-5 w-5 mr-3 text-blue-400" />
                        <span>{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <LoanBenefitsSection />

        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-12">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
              Remontoinnin parhaat puolet
            </span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[{
            url: "https://images.unsplash.com/photo-1530907487668-af02f65b4afe?auto=format&fit=crop&w=900&q=80",
            title: "Energiatehokkuus",
            description: "Säästä energiakustannuksissa uusilla ratkaisuilla"
          }, {
            url: "https://images.unsplash.com/photo-1586023492125-2f6f122e5912?auto=format&fit=crop&w=900&q=80",
            title: "Arvo nousee",
            description: "Laadukas remontti nostaa asunnon arvoa merkittävästi"
          }, {
            url: "https://images.unsplash.com/photo-1565183997392-2f6f122e5912?auto=format&fit=crop&w=900&q=80",
            title: "Toimivampi koti",
            description: "Modernit ratkaisut parantavat asumismukavuutta"
          }].map((item, i) => <div key={i} className="relative overflow-hidden rounded-xl group shadow-md">
                <AspectRatio ratio={16 / 9}>
                  <div className="w-full h-full bg-gray-200 animate-pulse absolute z-0"></div>
                  <Image src={item.url} alt={item.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 relative z-10" fallbackSrc={fallbackImages.general} priority={true} />
                </AspectRatio>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent p-6 z-20">
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white text-lg md:text-xl font-bold">{item.title}</h3>
                    <p className="text-white/90 text-sm">{item.description}</p>
                  </div>
                </div>
              </div>)}
          </div>

          <div className="mt-6">
            <RenovationImageGallery />
          </div>
        </section>

        <div className="mb-12">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }}>
            <RakennustarvikeAds />
          </motion.div>
        </div>

        <RenovationExamplesSection />

        <div className="mt-12 mb-12">
          <ZatapAdvertisement />
        </div>

        {/* Trust & Security Section */}
        <motion.section 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8 md:mb-16"
        >
          <div className="bg-gradient-to-r from-gray-50 to-white rounded-2xl p-6 md:p-8 border border-gray-100">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 text-gray-800">
              Miksi Lainasampo.fi on turvallinen valinta?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-gray-800">Rekisteröity välittäjä</h3>
                <p className="text-gray-600 text-sm">
                  Toimimme virallisena luotonvälittäjänä ja noudatamme kaikkia GDPR-vaatimuksia
                </p>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-gray-800">Yli 50 000 asiakasta</h3>
                <p className="text-gray-600 text-sm">
                  Olemme auttaneet tuhansia suomalaisia löytämään edullisimman lainan
                </p>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-yellow-600" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-gray-800">Maksuton palvelu</h3>
                <p className="text-gray-600 text-sm">
                  Kilpailutuspalvelumme on täysin maksuton - saat säästöt itsellesi
                </p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* UScore Advertisement Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 md:mb-16"
        >
          <UscoreAdvertisement />
        </motion.section>

        {/* Improved bottom section layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12 mb-12">
          <div className="lg:col-span-1 order-2 lg:order-1">
            <section className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl overflow-hidden relative h-full">
              <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
                <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                  <path fill="white" d="M47.1,-57.8C58.5,-45.7,63.7,-28.7,66.8,-11.1C70,6.5,71.1,24.5,63.5,38.6C55.9,52.7,39.6,62.8,22.2,67.6C4.9,72.3,-13.5,71.6,-29.6,64.7C-45.8,57.8,-59.6,44.7,-68.5,28C-77.4,11.3,-81.3,-9,-74.5,-25.3C-67.8,-41.6,-50.5,-54,-33.7,-65.1C-16.9,-76.2,-0.6,-86,14.8,-83.5C30.3,-81,35.7,-69.9,47.1,-57.8Z" />
                </svg>
              </div>
              <div className="relative p-6 md:p-8 text-white h-full flex flex-col justify-center">
                <h2 className="text-2xl md:text-3xl font-bold mb-4">Aloita remontti-unelmasi toteutus tänään!</h2>
                <p className="text-sm md:text-lg mb-6 max-w-2xl">
                  Kilpailuta lainatarjoukset ilmaiseksi ja ilman sitoumuksia. Saat lainapäätöksen nopeasti.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <LainaCta text={isMobile ? "Hae rahoitusta" : "Hae remonttilainaa"} className="bg-white text-blue-700 hover:bg-blue-50 font-bold" variant="primary" size={isMobile ? "md" : "lg"} />
                  {!isMobile && <Link to="/lainatuotteet" className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-blue-700 hover:bg-blue-800 border border-blue-500 text-white font-medium transition-all duration-300">
                      Vertaile lainatuotteita <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>}
                </div>
              </div>
            </section>
          </div>

          <div className="lg:col-span-1 order-1 lg:order-2">
            <div className="space-y-6">
              <Card className="border-blue-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
                  <CardTitle className="text-lg text-white">Remonttilaina lyhyesti</CardTitle>
                </div>
                <CardContent className="pt-5">
                  <div className="space-y-3">
                    {[{
                    label: "Laina-aika:",
                    value: "1-15 vuotta"
                  }, {
                    label: "Lainasumma:",
                    value: "1 000€ - 70 000€"
                  }, {
                    label: "Korko alkaen:",
                    value: "4,9%"
                  }, {
                    label: "Vakuus:",
                    value: "Vakuudeton tai asunto vakuutena"
                  }, {
                    label: "Käsittelyaika:",
                    value: "1-3 arkipäivää"
                  }].map((item, i) => <div key={i} className="flex justify-between py-1 border-b border-gray-100 last:border-0">
                        <span className="text-gray-600">{item.label}</span>
                        <span className="font-bold text-blue-800">{item.value}</span>
                      </div>)}
                  </div>

                  <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <span className="font-bold">Säästövinkki:</span> Kilpailuttamalla voit säästää jopa 30% rahoituksen kokonaiskustannuksissa!
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h3 className="font-semibold text-lg mb-4 text-gray-800">Suositut remonttikohteet</h3>
                <div className="grid grid-cols-1 gap-3">
                  {[{
                  type: "Keittiö",
                  highlight: "Modernit ja kestävät ratkaisut",
                  url: "/artikkelit/keittioremontin-suunnittelu"
                }, {
                  type: "Kylpyhuone",
                  highlight: "Vedeneristykset ja laatoitukset",
                  url: "/artikkelit/kylpyhuoneremontti"
                }, {
                  type: "Energiaremontti",
                  highlight: "Aurinkopaneelit ja lämpöpumput",
                  url: "/artikkelit/energiaremontti"
                }].map((dest, i) => <Link key={i} to={dest.url} className="flex flex-col p-3 rounded-lg bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-100 text-gray-800 hover:text-blue-700 transition-colors group">
                      <span className="font-bold">{dest.type}</span>
                      <span className="text-sm text-gray-500">{dest.highlight}</span>
                      <span className="text-xs text-blue-600 mt-1 group-hover:underline flex items-center">
                        Lue lisää
                        <ArrowRight className="ml-1 h-3 w-3" />
                      </span>
                    </Link>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};

export default RemonttilainaPage;
